import { subDays } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'
import differenceInCalendarDays from 'date-fns/esm/differenceInCalendarDays'
import isFuture from 'date-fns/isFuture'

import { isEmpty } from '@shared/common'
import {
  CustomInvoiceAdminDto,
  CustomInvoiceItemAdminDto,
  InvoiceAdminDto,
  InvoiceClientDto,
  InvoiceDashboardDto,
  InvoiceItemAdminDto,
  InvoicePetAdminDto,
  LocationPetProfileAdminDto,
  OrderAdminDto,
  PetInstructionDto,
  ResourceAdminDto,
} from 'service-api/src/types/api'
import { dayToJSDate, DEFAULT_TIMEZONE, getTzDateInUtc } from './dates'
import { i18WithDefault as t } from './locales'

type InvoiceAllowedAction = 'EDIT' | 'CANCEL' | 'CHECK_IN' | 'CHECK_OUT' | 'LATE_CHECK_IN' | 'LATE_CHECK_OUT' | 'VIEW'

type TPetEmojiOptions = {
  showMemorializeEmoji?: boolean
  showDeletedPetProfileEmoji?: boolean
}

const defaultPetEmojiOptions: TPetEmojiOptions = {
  showMemorializeEmoji: true,
  showDeletedPetProfileEmoji: true,
} as const

export const getInvoiceDates = (invoice: InvoiceClientDto | InvoiceAdminDto) => {
  const invoicePeriods = invoice.period && invoice.period.length > 0 ? invoice.period : invoice.requestPeriods
  const { startDate, startTime, endDate, endTime } = invoicePeriods?.[0] || {}
  return {
    startDate: getTzDateInUtc(startDate, startTime, invoice.location?.timezone),
    endDate: getTzDateInUtc(endDate, endTime, invoice.location?.timezone),
    hasStartTime: !!startTime,
    hasEndTime: !!endTime,
  }
}

export const getPlainInvoiceDates = (invoice: InvoiceClientDto | InvoiceAdminDto) => {
  const invoicePeriods = invoice.period && invoice.period.length > 0 ? invoice.period : invoice.requestPeriods
  const { startDate, startTime, endDate, endTime } = invoicePeriods?.[0] || {}
  return {
    startDate: dayToJSDate(startDate, startTime),
    endDate: dayToJSDate(endDate, endTime),
    hasStartTime: !!startTime,
    hasEndTime: !!endTime,
  }
}

export const getInvoiceRequestDates = (invoice: InvoiceClientDto) => {
  if (isEmpty(invoice.requestPeriods)) return []

  return invoice.requestPeriods!.map((period) => {
    return {
      startDate: getTzDateInUtc(period.startDate, period.startTime, invoice.location?.timezone),
      endDate: getTzDateInUtc(period.endDate, period.endTime, invoice.location?.timezone),
    }
  })
}

export const getInvoiceSchedule = (invoice: InvoiceClientDto) => {
  const { startDate, startTime, endDate, endTime } = invoice.resourceUsages?.[0] ?? {}
  if (!startDate) return
  return {
    startDate: getTzDateInUtc(startDate, startTime, invoice.location?.timezone),
    endDate: getTzDateInUtc(endDate, endTime, invoice.location?.timezone),
    duration: invoice.resourceUsages?.[0]?.duration || 0,
  }
}

export const getInvoiceActualDates = (invoice?: InvoiceAdminDto) => {
  const timezone = invoice?.location?.timezone || DEFAULT_TIMEZONE,
    period = invoice?.period?.[0],
    activity = invoice?.activities?.[0]
  const periodEndDate = isOvernight(invoice)
    ? getTzDateInUtc(period?.endDate, period?.endTime, timezone)
    : subDays(getTzDateInUtc(period?.endDate, period?.endTime, timezone), 1)
  return {
    startDate: activity?.startedAt
      ? new Date(activity?.startedAt)
      : getTzDateInUtc(period?.startDate, period?.startTime, timezone) || '',
    endDate: activity?.endedAt ? new Date(activity?.endedAt) : periodEndDate || '',
    hasStartTime: !!(activity?.startedAt || period?.startTime),
    hasEndTime: !!(activity?.endedAt || period?.endTime),
  }
}

export const getOfferAddons = (invoice?: InvoiceAdminDto): InvoiceItemAdminDto[] => {
  return invoice?.items?.filter((item) => item.offerType === 'SERVICE') || []
}

export const getOfferAddonsRegular = (invoice?: InvoiceAdminDto): InvoiceItemAdminDto[] => {
  return invoice?.items?.filter((item) => item.offerType === 'SERVICE' && !item.subtype) || []
}

export const getOfferAddonsRequired = (invoice?: InvoiceAdminDto): InvoiceItemAdminDto[] => {
  return invoice?.items?.filter((item) => item.offerType === 'SERVICE' && item.subtype === 'REQUIRED_ADDON') || []
}

export const getPetAddons = (invoice?: InvoiceAdminDto): InvoiceItemAdminDto[] => {
  return invoice?.items?.filter((item) => item.offerType === 'PET') || []
}

export const getCompCreditItems = (invoice?: InvoiceAdminDto): InvoiceItemAdminDto[] => {
  return invoice?.items?.filter((item) => item.type === 'CREDIT') || []
}

export const getAdditionItems = (invoice?: InvoiceAdminDto): InvoiceItemAdminDto[] => {
  return invoice?.items?.filter((item) => item.type === 'ADDITION') || []
}

export const getSchedulingItems = (invoice?: InvoiceAdminDto): InvoiceItemAdminDto[] => {
  return (
    invoice?.items?.filter(
      (item) =>
        item.type === 'OFFER' && item.locationServiceType?.serviceType?.usesScheduling && item.offerType !== 'PET'
    ) || []
  )
}

export const getRetailItems = (invoice: InvoiceAdminDto, existing: boolean): InvoiceItemAdminDto[] => {
  return (
    invoice?.items?.filter((item) => {
      if (item.type !== 'OFFER' || item.offerType !== 'PRIMARY') return false

      if (existing) {
        return !!item.offerId
      }
      return true
    }) || []
  )
}

export const getDiscountInvoiceItems = (invoice?: InvoiceAdminDto, selectedItem?: InvoiceItemAdminDto) => {
  return invoice?.items?.filter((item) => item?.parentItemId === selectedItem?.id && item?.type === 'DISCOUNT') || []
}

export const getCancellationFee = (invoice?: CustomInvoiceAdminDto): number => {
  const netPaid = invoice?.netPaid || 0
  return netPaid > 0 ? netPaid : 0
}

export type TInvoiceItemAddons = {
  id: string
  displayName: string
}

export const formatPetAddons = (invoiceItems?: InvoiceItemAdminDto[]) => {
  return (
    invoiceItems?.reduce((acc, item) => {
      const display = `${item.displayName} (${formatPetNames(getPetsInInvoiceItem(item), false, false)})`
      acc.push({
        id: item.id!,
        displayName: `${display}${(item.qty ?? 1) > 1 ? `x${item.qty}` : ''}`,
      })
      return acc
    }, [] as TInvoiceItemAddons[]) || []
  )
}

export const formatRunCardPetAddsOns = (petId: string, addOns?: InvoiceItemAdminDto[]) => {
  return addOns
    ?.filter((item) => {
      const hasPet = item.petRelations?.some((relation) => relation.invoicePet?.id === petId)
      return (item.offerType === 'PET' || item.offerType === 'SERVICE') && hasPet
    })
    .map((item) => {
      return `${item.displayName} ${item.qty === 1 ? '' : `(${item.qty}x)`}`
    })
}

// checks if invoice is ready for checkin
export const isCheckInState = (invoice?: CustomInvoiceAdminDto | InvoiceDashboardDto) =>
  ['CHECK_IN', 'LATE_CHECK_IN'].some((action) => invoice?.allowedActions?.includes(action as InvoiceAllowedAction))

// checks if invoice is ready for checkout
export const isCheckOutState = (invoice?: CustomInvoiceAdminDto | InvoiceDashboardDto) =>
  ['CHECK_OUT', 'LATE_CHECK_OUT'].some((action) => invoice?.allowedActions?.includes(action as InvoiceAllowedAction))

export const isViewState = (invoice?: CustomInvoiceAdminDto | InvoiceDashboardDto) =>
  !!invoice?.allowedActions?.includes('VIEW')

export const isCancelState = (invoice?: CustomInvoiceAdminDto | InvoiceDashboardDto) =>
  !!invoice?.allowedActions?.includes('CANCEL')

// checks if invoice has been checked in
export const hasUserCheckedIn = (invoice?: CustomInvoiceAdminDto) =>
  ['IN_PROGRESS', 'COMPLETED'].includes(invoice?.activities?.[0]?.activityStatus || '')

export const isSoldInvoice = (invoice?: CustomInvoiceAdminDto) => {
  return invoice?.invoiceSubStatus === 'SOLD'
}
export const getPrimaryOffer = (invoice?: InvoiceAdminDto): CustomInvoiceItemAdminDto | undefined => {
  return invoice?.items?.find((item) => item.offerType === 'PRIMARY')
}

export const getPrimaryOffers = (invoice?: InvoiceAdminDto): CustomInvoiceItemAdminDto[] => {
  return invoice?.items?.filter((item) => item.offerType === 'PRIMARY') || []
}

export const getPrimaryServiceType = (invoice?: InvoiceAdminDto) => {
  return invoice?.locationServiceType?.serviceType
}

export const getAssociatedInvoices = (order?: OrderAdminDto, invoiceId?: string) => {
  if (!invoiceId) {
    return []
  }

  const matchedInvoice = order?.invoices?.filter((invoice) => invoice.isInStay && invoice.parentInvoiceId === invoiceId)

  return matchedInvoice
}

export const getPayableGroupInvoices = (order?: OrderAdminDto) => {
  if (!order) {
    return []
  }

  return order.invoices?.filter((invoice) => (invoice.amountDue ?? 0) > 0) || []
}

export const getOrphanInvoices = (order?: OrderAdminDto) => {
  if (!order) {
    return []
  }

  // When the invoice is inStay and is declined/canceled, parentInvoice is set
  // to null but keeps inStay = true this makes getAssociatedInvoices not
  // return this invoices. So it's in inStay but no way to know to which invoice
  // was attached to
  return (
    order.invoices?.filter((invoice) => {
      const subStatus = getInvoiceSubStatus(invoice)
      if (invoice.isInStay && !invoice.parentInvoiceId && (subStatus.isDeclined || subStatus.isCanceled)) {
        return true
      }
      return false
    }) || []
  )
}

export const getPrimaryServiceTypeName = (
  invoice?: InvoiceAdminDto | InvoiceDashboardDto | PetInstructionDto['invoice']
) => {
  const serviceType = invoice?.locationServiceType?.serviceType as unknown as { name?: string } | undefined
  return serviceType?.name
}

export const isTemperTestInvoice = (invoice?: InvoiceAdminDto) => {
  return invoice?.locationServiceType?.serviceType?.name === 'temper-test'
}

export const getOtherOffers = (invoice?: InvoiceAdminDto): InvoiceItemAdminDto[] | undefined => {
  return invoice?.items?.reduce((acc, item) => {
    if (item.offerType !== 'PRIMARY') {
      acc.push(item)
    }
    return acc
  }, [] as InvoiceItemAdminDto[])
}

export const getLocationPetProfilesInInvoiceItem = (invoiceItem?: InvoiceItemAdminDto) => {
  return (
    invoiceItem?.petRelations
      ?.map((r) => r.invoicePet?.locationPetProfile)
      .filter((p): p is NonNullable<typeof p> => !!p && Object.keys(p).length > 0) || []
  )
}

export const getPetsInInvoiceItem = (invoiceItem?: InvoiceItemAdminDto) => {
  return (
    invoiceItem?.petRelations?.map((r) => ({
      ...r.invoicePet!,
    })) || []
  )
}

export const getItemsPetsInvoiced = (invoice?: InvoiceAdminDto): InvoicePetAdminDto[] => {
  const orderPetById = (invoice?.order?.pets || []).reduce(
    (acc, pet) => {
      if (pet.id) {
        acc[pet.id] = acc[pet.id] || pet
      }
      return acc
    },
    {} as Record<string, InvoicePetAdminDto>
  )
  return (
    getPrimaryOffer(invoice)?.petRelations?.map(({ invoicePet }) => {
      const orderPet = orderPetById[invoicePet?.id || ''] || {}
      return {
        ...invoicePet,
        locationPetProfile: {
          ...invoicePet?.locationPetProfile,
          petInstructions: orderPet.locationPetProfile?.petInstructions,
        },
      }
    }) || []
  )
}

export const getPetsInvoiced = (invoice?: InvoiceAdminDto): InvoicePetAdminDto[] => {
  const orderPetById = (invoice?.order?.pets || []).reduce(
    (acc, pet) => {
      if (pet.id) {
        acc[pet.id] = acc[pet.id] || pet
      }
      return acc
    },
    {} as Record<string, InvoicePetAdminDto>
  )

  return (getPrimaryOffer(invoice)?.petRelations || []).map(({ invoicePet, invoicePetId }) => {
    const petId = invoicePet?.id || invoicePetId || ''
    const orderPet = orderPetById[petId] || {}
    const tags = orderPet.locationPetProfile?.tags?.[0]?.tag?.id
      ? orderPet.locationPetProfile.tags
      : invoicePet?.locationPetProfile?.tags
    return {
      ...orderPet,
      locationPetProfile: {
        ...orderPet.locationPetProfile,
        profileImage: invoicePet?.locationPetProfile?.profileImage || orderPet.locationPetProfile?.profileImage,
        locationSpecies: orderPet.locationSpecies,
        tags,
      },
    }
  })
}

export const isOvernight = (invoice?: InvoiceAdminDto | InvoiceDashboardDto | PetInstructionDto['invoice']) => {
  const serviceType = invoice?.locationServiceType?.serviceType as unknown as { overnight?: boolean } | undefined
  return !!serviceType?.overnight
}

const DEFAULT_DATE_FORMAT = 'MMM d'
export const formatInvoicePeriod = (
  invoice?: InvoiceAdminDto,
  customFormat = DEFAULT_DATE_FORMAT,
  shouldShowDepartureTime = false
) => {
  const timezone = invoice?.location?.timezone || DEFAULT_TIMEZONE
  const primaryServiceType = getPrimaryServiceTypeName(invoice)
  const invoiceSubStatus = getInvoiceSubStatus(invoice)

  if (!invoice?.period?.[0] && !invoice?.requestPeriods?.[0]) {
    return ''
  }

  if (primaryServiceType === 'grooming' && (invoiceSubStatus.isRequested || invoiceSubStatus.isScheduled)) {
    const invoicePeriods = getInvoiceDates(invoice)
    const formatOption = invoicePeriods.hasStartTime ? customFormat : DEFAULT_DATE_FORMAT
    return formatInTimeZone(invoicePeriods.startDate, timezone, formatOption)
  }

  const { startDate, endDate, hasEndTime, hasStartTime } = getInvoiceDates(invoice)
  let finalTimeFormat = customFormat

  if (!hasStartTime && !hasEndTime) {
    finalTimeFormat = DEFAULT_DATE_FORMAT
  }

  const overNight =
    isOvernight(invoice) ||
    (primaryServiceType === 'grooming' && !!startDate && !!endDate) ||
    (shouldShowDepartureTime && hasStartTime && hasEndTime)

  return `${formatInTimeZone(startDate, timezone, finalTimeFormat)}${
    overNight ? ` - ${formatInTimeZone(endDate, timezone, shouldShowDepartureTime ? 'h:mmaaa' : finalTimeFormat)}` : ''
  }`
}

export const primaryOfferName = (invoice?: InvoiceAdminDto) => {
  return getPrimaryOffer(invoice)?.displayName || ''
}

export const getPrimaryInvoiceItem = (invoice?: InvoiceAdminDto) => {
  return invoice?.items?.find(
    (item) =>
      item.type === 'MEMBERSHIP' ||
      item.type === 'WALLET' ||
      (item.type === 'OFFER' && item.offerType === 'VOUCHER_OFFER') ||
      item.offerType === 'PRIMARY'
  )
}

const isLocationPetProfileMemorialized = (locationPetProfile?: LocationPetProfileAdminDto): boolean => {
  return locationPetProfile?.status === 'INACTIVE'
}

const isLocationPetProfileDeleted = (locationPetProfile?: LocationPetProfileAdminDto): boolean => {
  return isEmpty(locationPetProfile?.id) || isEmpty(locationPetProfile?.displayName)
}

export const getPetStatusEmoji = (
  locationPetProfile?: LocationPetProfileAdminDto,
  options?: TPetEmojiOptions
): string => {
  const config = { ...defaultPetEmojiOptions, ...options } as TPetEmojiOptions
  if (config.showMemorializeEmoji && isLocationPetProfileMemorialized(locationPetProfile)) return '🌈 '
  if (config.showDeletedPetProfileEmoji && isLocationPetProfileDeleted(locationPetProfile)) return '❌ '
  return ''
}

export const formatPetNames = (pets?: InvoicePetAdminDto[], showSex = true, showEmoji = true) => {
  const getLocalizedSexLabel = (sex: string) => {
    const localeSex = t(`global.label.${sex}`) || ''
    return localeSex ? ` (${localeSex[0]})` : ''
  }

  if (isEmpty(pets)) return ''

  return (
    pets
      ?.map((pet) => {
        if (isEmpty(pet)) return ''
        const sexStr = showSex ? getLocalizedSexLabel(pet?.locationPetProfile?.sex || '') : ''
        const emoji = showEmoji ? getPetStatusEmoji(pet?.locationPetProfile) : ''
        return `${emoji}${pet.locationPetProfile?.displayName || pet?.displayName || ''}${sexStr}`
      })
      .join(', ') || ''
  )
}

export const formatInvoiceSummaryTitle = (invoice: CustomInvoiceAdminDto) => {
  const timezone = invoice.location?.timezone || DEFAULT_TIMEZONE
  const offer = getPrimaryInvoiceItem(invoice),
    { startDate, endDate } = getInvoiceDates(invoice),
    overnight = isOvernight(invoice),
    pets = getPetsInvoiced(invoice)

  const date = overnight
    ? `${formatInTimeZone(startDate, timezone, 'M/d')} - ${formatInTimeZone(endDate, timezone, 'M/d')}`
    : formatInTimeZone(startDate, timezone, 'M/d')

  const petNames = formatPetNames(pets, false, false)
  return `${offer?.displayName} | ${date}${petNames ? ` | ${petNames}` : ''}`
}

export const getInvoiceItemsForDisplayName = (invoice: CustomInvoiceAdminDto) => {
  return (invoice.items || [])
    .filter((item) => item.type === 'OFFER')
    .map((item) => item.offer?.displayName)
    .join(', ')
}

export const formatInvoicePeriodSimple = (invoice?: InvoiceAdminDto, overNight = true) => {
  const timezone = invoice?.location?.timezone || DEFAULT_TIMEZONE
  if (!invoice?.period?.[0]) {
    return ''
  }
  const { startDate, endDate } = getInvoiceDates(invoice)
  return `${formatInTimeZone(startDate, timezone, 'M/d/yy')}${
    overNight ? ` - ${formatInTimeZone(endDate, timezone, 'M/d/yy')}` : ''
  }`
}

export const daysOfStay = (invoice?: InvoiceAdminDto, pricingStrategy?: 'NIGHTLY' | 'DAILY' | null) => {
  if (!invoice || !pricingStrategy) {
    return null
  }

  // no need to use startTime/endTime here. We are only interested in the calendar days difference.
  const { startDate, endDate } = invoice.period?.length ? invoice.period[0] : invoice.requestPeriods?.[0] || {}
  if (!startDate || !endDate) {
    return null
  }

  // no need to handle timezone since any offset will apply to both dates thus resulting in the same difference.
  const periodLength = differenceInCalendarDays(new Date(endDate), new Date(startDate))
  return pricingStrategy === 'DAILY' ? periodLength + 1 : periodLength
}

export const daysOfStayFromDates = (
  startDate?: Date | null,
  endDate?: Date | null,
  pricingStrategy?: 'NIGHTLY' | 'DAILY' | null
) => {
  if (!startDate || !endDate || !pricingStrategy) return null
  const periodLength = differenceInCalendarDays(endDate, startDate)
  return pricingStrategy === 'DAILY' ? periodLength + 1 : periodLength
}

export const hasInvoiceInFuture = (invoices?: InvoiceAdminDto[]) => {
  if (!invoices) {
    return false
  }

  return invoices.some((invoice) => isFuture(getInvoiceDates(invoice).startDate))
}

export const getServiceDatesAsText = (
  invoice: InvoiceAdminDto,
  options?: { includeTime?: boolean; useResourceUsage?: boolean }
) => {
  const serviceType = getPrimaryServiceTypeName(invoice)
  if (serviceType === 'wallet') return ''
  const timezone = invoice?.location?.timezone || DEFAULT_TIMEZONE
  let datesObj: { startDate: Date; endDate: Date; duration?: number } | undefined = undefined
  if (options?.useResourceUsage) {
    datesObj = getInvoiceSchedule(invoice)
  } else {
    datesObj = getInvoiceDates(invoice)
  }
  if (datesObj === undefined) return t('global.label.unscheduled')
  const format = options?.includeTime ? 'MMM d, yyyy h:mm aaa' : 'MMM d, yyyy'

  if (['daycare', 'grooming', 'temper-test', 'training'].includes(serviceType || '')) {
    return formatInTimeZone(datesObj.startDate, timezone, format)
  }

  return `${formatInTimeZone(datesObj.startDate, timezone, format)} - ${formatInTimeZone(
    datesObj.endDate,
    timezone,
    format
  )}`
}

export const isInvoiceCanceled = (invoice?: CustomInvoiceAdminDto) => {
  return invoice?.invoiceStatus === 'CANCELED' || invoice?.invoiceSubStatus === 'CANCELED'
}

export const isInvoiceConfirmed = (invoice?: CustomInvoiceAdminDto) => {
  return invoice?.invoiceStatus === 'CONFIRMED'
}

export const isInvoiceCompleted = (invoice?: CustomInvoiceAdminDto) => {
  return invoice?.activities?.[0]?.activityStatus === 'COMPLETED' || invoice?.invoiceSubStatus === 'COMPLETED'
}

export const isInvoiceInProgress = (invoice?: CustomInvoiceAdminDto) => {
  return invoice?.activities?.[0]?.activityStatus === 'IN_PROGRESS'
}

export const getInvoiceSubStatus = (invoice?: CustomInvoiceAdminDto | InvoiceDashboardDto) => {
  return {
    isCanceled: invoice?.invoiceSubStatus === 'CANCELED',
    isRequested:
      invoice?.invoiceSubStatus === 'REQUESTED' ||
      invoice?.invoiceSubStatus === 'PENDING_SCHEDULED' ||
      invoice?.invoiceSubStatus === null,
    isDeclined: invoice?.invoiceSubStatus === 'DECLINED',
    isScheduled: invoice?.invoiceSubStatus === 'SCHEDULED',
    isCheckedOut: invoice?.invoiceSubStatus === 'COMPLETED',
    isInProgress: invoice?.invoiceSubStatus === 'IN_PROGRESS',
    isPending: invoice?.invoiceSubStatus === 'PENDING',
    isBooked: invoice?.invoiceSubStatus === 'BOOKED',
    isPendingScheduled: invoice?.invoiceSubStatus === 'PENDING_SCHEDULED',
    isSold: invoice?.invoiceSubStatus === 'SOLD',
  }
}

export const getScheduleDate = (invoice?: CustomInvoiceAdminDto) => {
  if (!invoice) {
    return null
  }
  const { isRequested, isDeclined, isScheduled } = getInvoiceSubStatus(invoice)
  if (isRequested || isDeclined) {
    return null
  }

  const { startTime } = invoice.period?.[0] ?? {}
  if (!isScheduled && !startTime) {
    return null
  }

  const { startDate } = getInvoiceDates(invoice)
  const timezone = invoice?.location?.timezone || DEFAULT_TIMEZONE

  return formatInTimeZone(startDate, timezone, 'MMM d, yyyy h:mm aaa')
}

export const isViewOnly = (invoice: CustomInvoiceAdminDto) => {
  return invoice.allowedActions?.length === 1 && invoice.allowedActions[0] === 'VIEW'
}

export const isEditOnly = (invoice: CustomInvoiceAdminDto) => {
  return invoice.allowedActions?.length === 1 && invoice.allowedActions[0] === 'EDIT'
}

export const getInvoiceItem = (invoice?: CustomInvoiceAdminDto, invoiceItemId?: string | null) => {
  if (!invoiceItemId) {
    return undefined
  }
  return invoice?.items?.find((item) => item.id === invoiceItemId)
}

export const isVisitInvoice = (invoice?: InvoiceAdminDto | CustomInvoiceAdminDto | InvoiceDashboardDto) => {
  if (invoice?.locationServiceType?.serviceType && 'isVisit' in invoice.locationServiceType.serviceType) {
    return !!invoice.locationServiceType.serviceType.isVisit
  }
  return false
}

export const isSchedulingInvoice = (invoice?: InvoiceAdminDto | CustomInvoiceAdminDto) => {
  return !!invoice?.locationServiceType?.serviceType?.usesScheduling
}

export const getInvoiceEventPeriod = (invoice?: InvoiceAdminDto | CustomInvoiceAdminDto) => {
  if (Array.isArray(invoice?.period) && invoice?.period?.length > 0) {
    return invoice.period[0]
  }

  return (invoice?.requestPeriods ?? [])[0]
}

export const getResourceUsages = (
  invoice?: InvoiceAdminDto | CustomInvoiceAdminDto,
  type: ResourceAdminDto['type'] = 'PERSON',
  subType: ResourceAdminDto['subtype'] = 'GROOMING_STAFF',
  status: ResourceAdminDto['status'] = 'ACTIVE',
  checkGenericStaff: boolean = false
) => {
  if (!invoice) {
    return []
  }

  return (
    invoice.resourceUsages?.filter(
      (ru) =>
        ru.resource?.type === type &&
        (checkGenericStaff
          ? ru.resource?.subtype === subType || ru.resource?.subtype === null
          : ru.resource?.subtype === subType) &&
        ru.resource?.status === status
    ) || []
  )
}

export const getInvoicedPetProfiles = (invoice?: InvoiceAdminDto): InvoicePetAdminDto[] => {
  const primaryOffer = getPrimaryOffer(invoice)
  if (!primaryOffer?.petRelations) {
    return []
  }

  return primaryOffer.petRelations
    .map((relation) => relation.invoicePet)
    .filter((invoicedPet): invoicedPet is InvoicePetAdminDto => invoicedPet != null)
}

export const isRoomRequiredAddon = (invoiceItem?: InvoiceItemAdminDto) => {
  return invoiceItem?.subtype === 'REQUIRED_ADDON' && invoiceItem?.offerType === 'SERVICE'
}
